import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  Signal,
  ContentChild,
  DestroyRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Pagination } from '../../services/api/api';
import { UtilService } from '../../services/util.service';
import { DiscussionService } from '../../services/discussion.service';
import { QueryParamService } from '../../services/lists/query-params';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { ModalNavigationComponent } from '../../services/gui/modal/modal-navigation-component';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import {
  SbBooleanToggle,
  SbBooleanToggleModel,
  SbBooleanToggleViewMode,
  TableWrapperOptions,
  ViewSetting,
} from '../../services/global/global-interfaces';
import { SbFooterMoreWrapperDirective } from './table-wrapper-new/sb-table-wrapper-new.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SizeService } from '../../services/size-detection/size.service';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';

@Directive({
  selector: '[header-more]',
  standalone: false,
})
export class SbHeaderMoreWrapperDirective {
  @Input() classes: string;
}

@Component({
  selector: 'sb-table-wrapper',
  templateUrl: './sb-table-wrapper.component.html',
  styleUrls: ['./sb-table-wrapper.component.scss'],
  standalone: false,
})
export class SbTableWrapperComponent implements OnInit, AfterViewInit, OnDestroy, ModalNavigationComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly sizeService = inject(SizeService);

  @Input() isInModalContext: boolean;
  @Input() isInIntegratedContext: boolean;

  @ContentChild(SbHeaderMoreWrapperDirective, { static: false }) headerMore?: SbHeaderMoreWrapperDirective;
  @ContentChild(SbFooterMoreWrapperDirective, { static: false }) footerMore?: SbFooterMoreWrapperDirective;
  @ContentChild('thead') thead: TemplateRef<any>;
  @ContentChild('additionalInputTop') additionalInputTop: TemplateRef<any>;
  @ContentChild('tCardHead') tCardHead: TemplateRef<any>;
  @ContentChild('tbody') tbody: TemplateRef<any>;
  @ContentChild('card') card: TemplateRef<any>;
  @ContentChild('singleCard') singleCard: TemplateRef<any>;
  @ContentChild('singleRow') singleRow: TemplateRef<any>;
  @Input() headline: string;
  @Input() itemName: string;
  @Input() boolToggles: SbBooleanToggleModel[];
  @Input() headerClasses: string[];
  @Input() saveUserSettings: boolean = true;
  @Input() showExactMatch: boolean;
  @Input() iterateTableRows: boolean = true;
  @Input() hideSearch: boolean = false;
  @Input() hideHeadline: boolean = false;
  @Input() hidePagination: boolean = false;
  @Input() forceViewSetting: ViewSetting;
  @Input() startSearch: boolean = false;
  @Input() booleanToggleViewMode: SbBooleanToggleViewMode = 'default';
  @Output() boolChange: EventEmitter<SbBooleanToggle> = new EventEmitter<SbBooleanToggle>();
  @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() optionsChange: EventEmitter<TableWrapperOptions> = new EventEmitter<TableWrapperOptions>();
  ghosts = [];
  paginationInitialized: boolean;
  maxSize: number;
  paginationString: string;
  sizes: number[] = [8, 12, 16, 20, 24];
  boundaryLinks: boolean = true;
  collectionSize: number;
  resultCount: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  queryParams: Params;
  queryParamsParsed: boolean = false;
  @Input() viewSettingList: string[];
  @Input() viewSettingCard: string[];
  @Input() renderSplitButton: boolean = true;
  private readonly destroy$ = new Subject();

  screenSize = toSignal(this.sizeService.onResize$().pipe(takeUntilDestroyed(this.destroyRef)));
  viewSettingObjectFromUserSetting = computed(() => {
    const viewSettingObject = this.userService.viewSettingObject();
    const name = this.itemName;
    const itemName = name ? name + 'ViewSetting' : null;
    if (itemName && viewSettingObject[itemName]) {
      return { view: viewSettingObject[itemName], default: false };
    }
    return { view: viewSettingObject.defaultView ?? 'list', default: true };
  });

  currentViewSetting: Signal<ViewSetting> = computed(() => {
    const currentViewSetting = this.viewSettingObjectFromUserSetting();
    const forcedViewSetting = this.forceViewSetting;
    const screenSize = this.screenSize();
    if (forcedViewSetting) {
      return forcedViewSetting;
    }
    if (['xs', 'sm', 'md'].includes(screenSize)) {
      return 'card';
    }
    let viewSetting: ViewSetting;
    if (this.isInModalContext) {
      viewSetting = 'list';
    } else if (this.isInIntegratedContext) {
      viewSetting = 'list';
    } else if (currentViewSetting.view) {
      viewSetting = currentViewSetting.view;
    } else {
      viewSetting = 'list';
    }
    return viewSetting;
  });

  isCardDisplayed = computed(() => {
    if (!this.card) {
      // list without cards
      return false;
    }
    return this.currentViewSetting() === 'card';
  });

  constructor(
    private cdRef: ChangeDetectorRef,
    public util: UtilService,
    public discussionService: DiscussionService,
    private queryParamService: QueryParamService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private translate: TranslateService
  ) {}

  // tslint:disable-next-line:variable-name
  _pagination: BehaviorSubject<Pagination>;

  get pagination(): BehaviorSubject<Pagination> {
    return this._pagination;
  }

  set pagination(val: BehaviorSubject<Pagination>) {
    this._pagination = val;
  }

  // tslint:disable-next-line:variable-name
  _options: TableWrapperOptions = {
    search: null,
    pagination: null,
    exactMatch: false,
  };

  get options() {
    return this._options;
  }

  @Input() set options(val: TableWrapperOptions) {
    if (val) {
      if (val.pagination) {
        this._options.pagination = val.pagination;
      }
      if (val.search) {
        this._options.search = val.search;
      }
      if (val.exactMatch) {
        this._options.exactMatch = val.exactMatch;
      }
    }
  }

  // tslint:disable-next-line:variable-name
  _itemsData: any[];

  get itemsData(): any[] {
    return this._itemsData;
  }

  @Input() set itemsData(val: any[]) {
    this._itemsData = val;
    if (this._itemsData) {
      if (this.options.pagination) {
        this.paginationInitialized = true;
      }
    }
    this.paginationString = this.util.getPaginationString(
      this.page,
      this.maxSize,
      this.itemsData?.length,
      this.resultCount,
      this.itemName ? this.itemName : 'items'
    );
    this.destroySub();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginationString = this.stringTransformation.getPaginationString(
        this.page,
        this.maxSize,
        this.itemsData?.length,
        this.resultCount,
        this.itemName ? this.itemName : 'items'
      );
    });
  }

  // tslint:disable-next-line:variable-name
  _loading: boolean;

  get loading(): boolean {
    return this._loading;
  }

  @Input() set loading(val: boolean) {
    this._loading = val;
    if (this.loading) {
      if (!this.itemsData) {
        this.ghosts = new Array(this.maxSize);
      }
    } else {
      this.ghosts = [];
    }
  }

  // tslint:disable-next-line:variable-name
  _page: number = 1;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.options.pagination.value.page = val;
  }

  saveMaxSize(): void {
    if (this.itemName && this.saveUserSettings) {
      this.userService.setUserSetting(this.itemName + 'MaxSize', this.maxSize.toString()).then();
    }
  }

  ngOnInit(): void {
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.queryParamsParsed = true;
        if (this.router.url.includes('admin')) {
          this.maxSize = 8;
          this.initPagination().then(() => {
            this.requestDebounced();
          });
        } else {
          this.userService.getAllUserSettings().then(data => {
            if (data) {
              const e = data[this.itemName + 'MaxSize'];
              if (this.singleRow || this.singleCard) {
                this.maxSize = e ? (this.detectSingleElementItemSize() ? +e - 1 : +e) : 7;
                this.sizes = [7, 11, 15, 19, 23, 50, 100];
              } else {
                this.maxSize = e ? +e : 8;
                this.sizes = [8, 12, 16, 20, 24, 50, 100];
              }
              this.initPagination().then(() => {
                this.requestDebounced();
              });
            }
          });
        }
      });
    }
  }

  detectSingleElementItemSize() {
    if (this.singleRow || this.singleCard) {
      return this.maxSize % 2 === 0;
    }
  }

  destroySub() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {}

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      if (!this.isInModalContext && !this.isInIntegratedContext) {
        this.parseSearchParams();
        this.parsePageParams();
        this.parseBoolQueryParams();
      }
      res();
    });
  }

  exactMatchChange(t: string, $e) {
    switch (t) {
      case 'exactMatch':
        this.options.exactMatch = $e;
        break;
    }
    this.requestDebounced();
  }

  parseSearchParams() {
    this.options.search = this.queryParamService.getQueryParam('search');
  }

  parsePageParams() {
    try {
      const pageStr: string = this.queryParamService.getQueryParam('page');
      this._page = +pageStr;
    } catch (e) {
      this._page = 1;
    }
    if (!this._page) {
      this._page = 1;
    }
  }

  initPagination(): Promise<void> {
    return new Promise(res => {
      this.options.pagination = new BehaviorSubject<Pagination>({
        page: this.page,
        count: this.maxSize ? this.maxSize : 8,
        pages: -1,
        resultcount: -1,
      });
      this.options.pagination.subscribe((e: Pagination) => {
        this.collectionSize = e.resultcount;
        this.maxSize = e.count;
        this.resultCount = e.resultcount;
      });
      res();
    });
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  setMaxSize(size) {
    this.maxSize = size;
    this.saveMaxSize();
    this.options.pagination.value.count = size;
    this.requestDebounced();
  }

  doSearch(searchString: string) {
    this.options.search = searchString;
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.queryParamsParsed && !this.isInModalContext && !this.isInIntegratedContext) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      // options push
      if (this.optionsChange) {
        this.optionsChange.emit(this.options);
      }
      clearTimeout(this.debounceTimer);
    }, 500);
  }

  parseBoolQueryParams() {
    if (this.showExactMatch) {
      const pExactMatch: string = this.queryParamService.getQueryParam('exactMatch');
      if (pExactMatch === 'true') {
        this.options.exactMatch = true;
      } else if (pExactMatch === 'false') {
        this.options.exactMatch = false;
      } else {
        this.options.exactMatch = false;
      }
    }
  }

  refreshQueryParams() {
    if (!this.isInModalContext && !this.isInIntegratedContext) {
      if (this.options.search) {
        this.queryParamService.setQueryParam('search', this.options.search);
      } else {
        this.queryParamService.clearQueryParam('search');
      }

      if (this.page) {
        this.queryParamService.setQueryParam('page', '' + this.page);
      } else {
        this.queryParamService.clearQueryParam('page');
      }
      if (this.showExactMatch) {
        this.refreshBoolQueryParam(
          'exactMatch',
          this.options.exactMatch === true ? true : this.options.exactMatch === false ? false : false
        );
      }
    }
  }

  refreshBoolQueryParam(name: string, value: boolean) {
    if (value === true || value === false) {
      this.queryParamService.setQueryParam(name, '' + value);
    } else {
      this.queryParamService.clearQueryParam(name);
    }
  }

  ngOnDestroy(): void {
    // this.util.viewSettingService.viewSettingObj.next(null);
    // this.util.destroySubscription(this.viewSettingSubscription);
    // this.util.destroySubscription(this.queryParamsSub);
    clearTimeout(this.debounceTimer);
  }

  isSplitButtonVisible(): boolean {
    return !this.isInIntegratedContext && this.renderSplitButton && this.card && !this.isInModalContext;
  }
}
